import { ItemGroup } from 'designSystem/components/Item'
import { Text } from 'designSystem/components/Text'
import { useTicketLifecycle } from 'features/libra/lifecycle/TicketLifecycleContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { SignoffPreview } from './SignoffCard'
import { SignoffCard } from './SignoffCard'
import { resolveSignoffRequirements } from './signoffRequirements'
import type { SignoffRow } from './signoffState'
import { resolveSignoffRows } from './signoffState'
import type { SignoffConfig, SignoffRecord } from './signoffTypes'
import { useTicketSignoffs } from './TicketSignoffsContext'

/**
 * Collects N independent signatures on a ticket. Not a workflow step — it is
 * rendered as its own drawer by a host step (see `NiiReview`), so sign-off
 * never appears in the step bar and never gates step navigation.
 */
export const SignoffPanel: FC<{ config: SignoffConfig }> = ({ config }) => {
  const { fieldValues } = useTicketAccess()
  const { currentStage, groupId, userHasRoles } = useTicketLifecycle()
  const { signoffs, isLoading, sign } = useTicketSignoffs()
  const { user } = useGeckoPermissions()
  const { errorNotification } = useGeckoNotifications()
  const [signingSlotId, setSigningSlotId] = useState<string | null>(null)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const requirements = useMemo(
    () => resolveSignoffRequirements(config.signoffs, signoffs, fieldValues),
    [config.signoffs, signoffs, fieldValues]
  )

  const rows = useMemo(
    () =>
      resolveSignoffRows({
        requirements,
        stageId: currentStage?.id,
        enabledStages: config.enabledStages,
        groupId,
        userHasRoles,
      }),
    [
      requirements,
      config.enabledStages,
      currentStage?.id,
      groupId,
      userHasRoles,
    ]
  )

  // Counted over the required slots only, so a waived slot doesn't leave the
  // tally permanently short of its total.
  const progress = useMemo(() => {
    const required = rows.filter((row) => row.required)
    return {
      signed: required.filter((row) => row.status === 'signed').length,
      total: required.length,
    }
  }, [rows])

  // What an unsigned slot tells the signer it will record. The stored
  // `signedAt` is stamped at click time, so this is a preview, not the value.
  const preview = useMemo<SignoffPreview | undefined>(
    () =>
      user
        ? {
            name: user.name ?? user.email,
            date: new Date().toLocaleDateString(),
          }
        : undefined,
    [user]
  )

  const handleSign = useCallback(
    async (row: SignoffRow) => {
      if (!user) return
      const record: SignoffRecord = {
        userId: user.id,
        userName: user.name ?? user.email,
        signedAt: new Date().toISOString(),
      }
      setSigningSlotId(row.slot.id)
      try {
        await sign(row.slot.id, record)
      } catch (err) {
        errorNotification(
          `Failed to sign as ${row.slot.title}: ${
            err instanceof Error ? err.message : String(err)
          }`
        )
      } finally {
        if (mountedRef.current) setSigningSlotId(null)
      }
    },
    [sign, errorNotification, user]
  )

  if (isLoading) {
    return (
      <Text variant='caption' color='muted'>
        Loading sign-offs…
      </Text>
    )
  }

  return (
    <ItemGroup>
      <Text variant='caption' color='muted'>
        {progress.signed} of {progress.total} required sign-offs complete
      </Text>
      {rows.map((row) => (
        <SignoffCard
          key={row.slot.id}
          slot={row.slot}
          status={row.status}
          required={row.required}
          lock={row.lock}
          record={row.record}
          previous={row.previous}
          preview={preview}
          loading={signingSlotId === row.slot.id}
          disabled={signingSlotId !== null || !user}
          onSign={() => void handleSign(row)}
        />
      ))}
    </ItemGroup>
  )
}
